import { useState } from "react";
import axios from "axios";
import { Gift, Loader2, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn } from "@/components/Reveal";
import { EXPERIENCES } from "@/data/experiences";
import { LOOKUP_BY_SLUG } from "@/components/BookingModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPEN_AMOUNTS = [50, 100, 250];

const priceOf = (e) =>
  Number(
    ((e.details.find((d) => d.label === "Price") || {}).value || "").match(/€(\d+)/)?.[1] || 0
  );

const inputCls = "w-full border border-white/10 bg-navy-900 px-4 py-3 text-sm text-ivory placeholder:text-mist/60 focus:border-gold focus:outline-none";
const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.24em] text-mist";

function ChoiceCard({ selected, onClick, testId, eyebrow, title, sub, price }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={selected}
      className={`h-full w-full border p-6 text-left transition-all duration-300 ${
        selected ? "border-gold bg-gold/10" : "border-white/10 bg-navy-900 hover:border-gold/50"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">{eyebrow}</p>
      <p className="mt-2 font-serif text-xl text-ivory sm:text-2xl">{title}</p>
      <p className="mt-1 text-sm font-light text-mist">{sub}</p>
      <p className="mt-4 font-serif text-2xl text-gold">{price}</p>
    </button>
  );
}

export default function VouchersPage() {
  const [sel, setSel] = useState(null); // { kind, lookupKey?, amount, title, pricePerPerson? }
  const [people, setPeople] = useState(2);
  const [form, setForm] = useState({ buyerName: "", buyerEmail: "", recipientName: "", recipientEmail: "", giftMessage: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const total = sel ? (sel.kind === "activity" ? sel.pricePerPerson * people : sel.amount) : 0;

  const buy = async () => {
    if (!sel) {
      setError("Choose a voucher option first");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        kind: sel.kind,
        lookup_key: sel.lookupKey || "",
        people,
        amount: (sel.kind === "open" ? sel.amount : 0) * 100,
        origin_url: window.location.origin,
        buyer_name: form.buyerName,
        buyer_email: form.buyerEmail,
        recipient_name: form.recipientName,
        recipient_email: form.recipientEmail,
        gift_message: form.giftMessage,
      };
      const { data } = await axios.post(`${API}/vouchers/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went adrift — please try again");
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero eyebrow="The Perfect Christmas Gift" title="Gift Vouchers" image="/images/hero-aerial.jpg">
        Give someone their own island adventure — sea safaris, high-speed RIB rides, kayaking, SUP
        and our wood-fired beach sauna. Delivered straight to your inbox, ready to print or forward.
      </PageHero>

      <section className="bg-navy-950 py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            <FadeIn>
              <div>
                <Eyebrow>How it works</Eyebrow>
                <ol className="mt-8 space-y-6">
                  {[
                    ["Choose", "Pick an activity and how many people, or an open value they can spend on any adventure."],
                    ["Pay securely", "Checkout with Stripe — card, Apple Pay or Google Pay."],
                    ["Voucher arrives", "A printable voucher with a unique code lands in your inbox instantly. Forward it, print it, wrap it."],
                  ].map(([t, d], i) => (
                    <li key={t} className="flex gap-5">
                      <span className="font-serif text-3xl text-gold/70">0{i + 1}</span>
                      <div>
                        <p className="font-serif text-xl text-ivory">{t}</p>
                        <p className="mt-1 text-sm font-light leading-relaxed text-mist">{d}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="mt-10 flex items-start gap-4 border border-gold/25 bg-navy-900 p-5">
                  <Sparkles className="mt-1 h-5 w-5 shrink-0 text-gold" />
                  <p className="text-sm font-light leading-relaxed text-mist">
                    Valid for 12 months. To book, the lucky recipient just quotes their voucher code by phone or email —
                    we&rsquo;ll find a day (and a tide!) that suits.
                  </p>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="space-y-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">1 · Choose your voucher</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  {EXPERIENCES.map((e) => (
                    <ChoiceCard
                      key={e.slug}
                      testId={`voucher-activity-${e.slug}`}
                      selected={sel && sel.kind === "activity" && sel.lookupKey === LOOKUP_BY_SLUG[e.slug]}
                      onClick={() => setSel({ kind: "activity", lookupKey: LOOKUP_BY_SLUG[e.slug], title: e.title, pricePerPerson: priceOf(e) })}
                      eyebrow="Activity voucher"
                      title={e.title}
                      sub="Per person, any day they choose"
                      price={`€${priceOf(e)} per person`}
                    />
                  ))}
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">…or an open voucher</p>
                <div className="grid gap-4 sm:grid-cols-3">
                  {OPEN_AMOUNTS.map((a) => (
                    <ChoiceCard
                      key={a}
                      testId={`voucher-open-${a}`}
                      selected={sel && sel.kind === "open" && sel.amount === a}
                      onClick={() => setSel({ kind: "open", amount: a })}
                      eyebrow="Open value"
                      title="Any adventure"
                      sub="Spend on any activity"
                      price={`€${a}`}
                    />
                  ))}
                </div>

                {sel && sel.kind === "activity" && (
                  <div data-testid="voucher-people-row">
                    <label htmlFor="v-people" className={labelCls}>How many people?</label>
                    <select id="v-people" value={people} onChange={(e) => setPeople(Number(e.target.value))} data-testid="voucher-people-select" className={`${inputCls} appearance-none [&>option]:bg-navy-900`}>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.1}>
            <div className="mt-16 border border-gold/25 bg-navy-900 p-6 sm:p-10" data-testid="voucher-form">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">2 · Your details</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="v-buyer-name" className={labelCls}>Your name *</label>
                  <input id="v-buyer-name" required data-testid="voucher-buyer-name" value={form.buyerName} onChange={(e) => setForm({ ...form, buyerName: e.target.value })} className={inputCls} placeholder="Jane Murphy" />
                </div>
                <div>
                  <label htmlFor="v-buyer-email" className={labelCls}>Your email *</label>
                  <input id="v-buyer-email" type="email" required data-testid="voucher-buyer-email" value={form.buyerEmail} onChange={(e) => setForm({ ...form, buyerEmail: e.target.value })} className={inputCls} placeholder="jane@example.com" />
                </div>
                <div>
                  <label htmlFor="v-recipient-name" className={labelCls}>Recipient&rsquo;s name (optional)</label>
                  <input id="v-recipient-name" data-testid="voucher-recipient-name" value={form.recipientName} onChange={(e) => setForm({ ...form, recipientName: e.target.value })} className={inputCls} placeholder="Tom" />
                </div>
                <div>
                  <label htmlFor="v-recipient-email" className={labelCls}>Send a copy to the recipient (optional)</label>
                  <input id="v-recipient-email" type="email" data-testid="voucher-recipient-email" value={form.recipientEmail} onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })} className={inputCls} placeholder="tom@example.com" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="v-message" className={labelCls}>Gift message (optional)</label>
                  <textarea id="v-message" rows={2} maxLength={500} data-testid="voucher-gift-message" value={form.giftMessage} onChange={(e) => setForm({ ...form, giftMessage: e.target.value })} className={inputCls} placeholder="Merry Christmas — an island adventure awaits!" />
                </div>
              </div>
              <div className="mt-8 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-mist">Total</p>
                  <p className="font-serif text-4xl text-gold" data-testid="voucher-total">€{total}</p>
                </div>
                <button
                  type="button"
                  onClick={buy}
                  disabled={busy}
                  data-testid="voucher-buy-button"
                  className="inline-flex items-center justify-center bg-gold px-10 py-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-navy-800 transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
                >
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Gift className="mr-2 h-4 w-4" />}
                  {busy ? "Opening checkout…" : "Buy gift voucher"}
                </button>
              </div>
              {error && <p data-testid="voucher-error" className="mt-4 text-sm text-red-400">{error}</p>}
              <p className="mt-6 text-xs text-mist">Secure checkout by Stripe. Vouchers are valid for 12 months from purchase. Booking subject to availability and weather.</p>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
